import type { AxiosResponse } from 'axios';

import { API_URL } from '@/config/env';
import { conformanceApi } from '@/lib/api';

import type {
  AccountableJiraBoards,
  SystemCatalog,
  ViewSetting,
  ViewUpsertRequest,
} from './access.types';

/**
 * System configuration service (views + unified catalog).
 *
 * Calls the Conformance SystemConfigurationController @ `api/v4.0/system`:
 *   GET    /system/catalog       -> SystemCatalog (roles + views + applications)
 *   GET    /system/views         -> ViewSetting[]
 *   PUT    /system/views/{name}  -> ViewSetting  (idempotent upsert)
 *   DELETE /system/views/{name}  -> 204
 */

// `/system` routes hang off the API root, not the `/orr` segment.
const SYSTEM_BASE = `${API_URL.replace('/orr', '')}/system`;

export const getSystemCatalog = (): Promise<AxiosResponse<SystemCatalog>> =>
  conformanceApi.get<SystemCatalog>(`${SYSTEM_BASE}/catalog`);

export const getViews = (): Promise<AxiosResponse<ViewSetting[]>> =>
  conformanceApi.get<ViewSetting[]>(`${SYSTEM_BASE}/views`);

/** Accountable-team -> Jira project key map for Report-a-Bug (any authenticated user). */
export const getAccountableJiraBoards = (): Promise<
  AxiosResponse<AccountableJiraBoards>
> =>
  conformanceApi.get<AccountableJiraBoards>(`${SYSTEM_BASE}/jira-boards`);

/**
 * Resolve the Jira board (project key) for an accountable-team display value against the
 * data-driven map from {@link getAccountableJiraBoards}. Case-insensitive so it is robust
 * to key casing. Shared by Report-a-Bug and the evaluation sidebar — the mapping is never
 * hardcoded in the UI.
 */
export const resolveAccountableJiraBoard = (
  boards: AccountableJiraBoards | undefined,
  accountable: string | null | undefined,
): string | null => {
  if (!boards || !accountable) return null;
  const target = accountable.toLowerCase();
  const entry = Object.entries(boards).find(([team]) => team.toLowerCase() === target);
  return entry?.[1] ?? null;
};

export const upsertAccountableJiraBoards = (
  body: AccountableJiraBoards,
): Promise<AxiosResponse<AccountableJiraBoards>> =>
  conformanceApi.put<AccountableJiraBoards>(`${SYSTEM_BASE}/jira-boards`, body);

export const upsertView = (
  name: string,
  body: ViewUpsertRequest,
): Promise<AxiosResponse<ViewSetting>> =>
  conformanceApi.put<ViewSetting>(
    `${SYSTEM_BASE}/views/${encodeURIComponent(name)}`,
    body,
  );

export const deleteView = (name: string): Promise<AxiosResponse<void>> =>
  conformanceApi.delete<void>(
    `${SYSTEM_BASE}/views/${encodeURIComponent(name)}`,
  );
